/** Кто вошёл, в каком пространстве работает и какой канал сейчас выбран. */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setWorkspace, type Channel, type Me } from "./lib/api";

interface SessionValue {
  me: Me | null;
  loading: boolean;
  channels: Channel[];
  channel: Channel | null;
  channelId: number | null;
  selectChannel: (id: number) => void;
  selectWorkspace: (id: number) => void;
  isPro: boolean;
  can: (permission: string) => boolean;
  refresh: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

const CHANNEL_KEY = "tca.channel";
const WORKSPACE_KEY = "tca.workspace";

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [workspaceId, setWorkspaceId] = useState<number | null>(() => {
    const stored = localStorage.getItem(WORKSPACE_KEY);
    return stored ? Number(stored) : null;
  });
  const [channelId, setChannelId] = useState<number | null>(() => {
    const stored = localStorage.getItem(CHANNEL_KEY);
    return stored ? Number(stored) : null;
  });

  setWorkspace(workspaceId);

  const meQuery = useQuery({
    queryKey: ["me", workspaceId],
    queryFn: () => api.get<Me>("/auth/me"),
    retry: false,
  });

  const channelsQuery = useQuery({
    queryKey: ["channels", workspaceId],
    queryFn: () => api.get<{ channels: Channel[] }>("/channels"),
    enabled: Boolean(meQuery.data),
  });

  const channels = channelsQuery.data?.channels ?? [];

  useEffect(() => {
    if (!channels.length) return;
    if (channelId && channels.some((item) => item.id === channelId)) return;
    setChannelId(channels[0].id);
    localStorage.setItem(CHANNEL_KEY, String(channels[0].id));
  }, [channels, channelId]);

  const value = useMemo<SessionValue>(() => {
    const me = meQuery.data ?? null;
    const channel = channels.find((item) => item.id === channelId) ?? null;
    return {
      me,
      loading: meQuery.isLoading,
      channels,
      channel,
      channelId: channel?.id ?? null,
      isPro: me?.workspace?.plan === "pro" || Boolean(me?.user.is_superuser),
      can: (permission: string) => Boolean(me?.permissions.includes(permission)),
      selectChannel: (id: number) => {
        setChannelId(id);
        localStorage.setItem(CHANNEL_KEY, String(id));
      },
      selectWorkspace: (id: number) => {
        setWorkspaceId(id);
        setWorkspace(id);
        localStorage.setItem(WORKSPACE_KEY, String(id));
        setChannelId(null);
        localStorage.removeItem(CHANNEL_KEY);
        queryClient.clear();
      },
      refresh: () => {
        queryClient.invalidateQueries();
      },
    };
  }, [meQuery.data, meQuery.isLoading, channels, channelId, queryClient]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession вне SessionProvider");
  return value;
}
